import { useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLeaflet } from 'react-leaflet';
import { useMap } from 'react-map-gl';
import { isEqual, zipObject } from 'lodash-es';
import { zoomTo } from '#/map/utils/maplibre';
import mapConfig from '#/map-config';
import { BoundOptionsContext } from '#/map/providers/BoundOptionsProvider';
import { getMapBoundsExtent, panToWithOffset } from '#/map/utils/leaflet';
import { setFocusedOnHighlightedStop } from '#/transit-tracker/slice';
import featureFlags from '#/shared/feature-flags';

const { mapLibre } = featureFlags;

/**
 * Zoom and/or pan to the supplied marker on instantiation and when its
 * coordinates change
 * @param {{lat: number, lon: number}} marker
 * @param {boolean} [centerOnMap]
 * @param {boolean} [preventSnapback]
 */
const useCenterOnMap = (
  marker,
  centerOnMap = true,
  preventSnapback = false
) => {
  const previousLocationRef = useRef();
  const dispatch = useDispatch();
  const { map: leafletMap } = useLeaflet();
  const { current: mapLibreMap } = useMap();
  const map = mapLibre ? mapLibreMap : leafletMap;
  const options = useContext(BoundOptionsContext);
  const { poiBoundsExtent } = mapConfig;
  const { highlightedStop, focusedOnHighlightedStop } = useSelector(s => ({
    highlightedStop: s.transitTracker.highlightedStop,
    focusedOnHighlightedStop: s.transitTracker.focusedOnHighlightedStop,
  }));
  const poiCoords = useMemo(
    () => centerOnMap && [marker.lat, marker.lon],
    [centerOnMap, marker.lat, marker.lon]
  );

  useEffect(() => {
    // if centerOnMap is false, the method exits with return
    if (!centerOnMap) return;
    // if the current zoom is less than the result from fitting-to-bounds
    // zoom in, otherwise pan
    const adjustMap = () => {
      if (!poiCoords) return;
      if (mapLibre) {
        const newLocation = zipObject(['lat', 'lon'], poiCoords);
        if (!isEqual(previousLocationRef.current, newLocation) && poiCoords) {
          zoomTo(
            map,
            { lat: poiCoords[0], lon: poiCoords[1] },
            mapLibre ? {} : options
          );
          previousLocationRef.current = newLocation;
        }
      } else if (
        getMapBoundsExtent(map, options) > poiBoundsExtent &&
        poiCoords
      ) {
        zoomTo(
          map,
          { lat: poiCoords[0], lon: poiCoords[1] },
          mapLibre ? {} : options
        );
      } else {
        panToWithOffset(
          map,
          poiCoords,
          [options.centerOffsetX, 0],
          mapLibre ? {} : options
        );
      }
    };
    // this keeps the map from shifting when switching between stop marker icons
    // due to changing zoom levels causing re-instantiation of this hook
    if (preventSnapback && highlightedStop && !focusedOnHighlightedStop) {
      dispatch(setFocusedOnHighlightedStop(true));
      adjustMap();
    } else if (!preventSnapback) {
      adjustMap();
    }
  }, [
    poiCoords,
    map,
    dispatch,
    options,
    centerOnMap,
    poiBoundsExtent,
    focusedOnHighlightedStop,
    preventSnapback,
    highlightedStop,
  ]);
};

export default useCenterOnMap;
